/**
 * On-Device Reminders — list (FEAT-REMINDERS-001). Content → On-Device Reminders.
 * Mirrors the In-App Messages list shell; the "When" column renders the engine's
 * timing sentence. Status honesty: Published / Draft / Paused, never Sent/Delivered.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Flex, HStack, Button, IconButton, Text, Input, InputGroup, InputLeftElement, Select,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer, Tag, Tooltip, Spinner, Center, Menu,
  MenuButton, MenuList, MenuItem, useToast, Badge, CloseButton, useDisclosure,
} from '@chakra-ui/react';
import { FiPlus, FiSearch, FiBell, FiEdit2, FiTrash2, FiCopy, FiSend, FiPause, FiPlay, FiMoreVertical, FiBarChart2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import ConfirmDialog from '../../components/ConfirmDialog';
import { usePermissionRouteContext } from '../../components/PermissionRoute';
import usePagePermissions from '../../hooks/usePagePermissions';
import { hasPermission } from '../../utils/DefaultFunction';
import remindersApi from './api';
import { apiToForm } from './mappers';
import { timingSentence } from './preview/engine';
import ReminderTypePicker from './ReminderTypePicker';
import { normalizeReminderStats, formatCount, formatPct, deliveredShort } from './stats';
import PageHeading from '../../components/shared/PageHeading';
import { LuAlarmClock } from 'react-icons/lu';

const STATUS_META = {
  active: { label: 'Published', scheme: 'green', hint: 'Phones pick this up on their next app open.' },
  draft: { label: 'Draft', scheme: 'gray', hint: 'Not sent to phones.' },
  paused: { label: 'Paused', scheme: 'orange', hint: 'Phones remove its reminders on their next app open.' },
  archived: { label: 'Archived', scheme: 'gray', hint: '' },
};
const TYPE_LABEL = { event: 'Event', schedule: 'Repeating schedule', prayer: 'Prayer & Islamic calendar', inactivity: 'App not opened', first_open: 'Since first open', profile_date: 'Profile date', milestone: 'App usage milestone' };
const TABS = [['all', 'All'], ['active', 'Published'], ['draft', 'Draft'], ['paused', 'Paused']];
const rel = (d) => {
  if (!d) return '—';
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now'; if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function ReminderList() {
  const toast = useToast();
  const navigate = useNavigate();
  const routeCtx = usePermissionRouteContext();
  const fallback = usePagePermissions(routeCtx ? null : 'content/reminders');
  const can = useCallback((slug) => (routeCtx ? hasPermission(slug, routeCtx.permissions) : fallback.can(slug)), [routeCtx, fallback]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [stats, setStats] = useState({});
  const [banner, setBanner] = useState(() => { try { return localStorage.getItem('reminders_sdk_banner') !== 'off'; } catch { return true; } });
  const [target, setTarget] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const picker = useDisclosure();
  const onPickType = (c) => { picker.onClose(); navigate(`/content/reminders/new?type=${c.type}${c.mode ? `&mode=${c.mode}` : ''}`); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await remindersApi.list();
      const list = res.data?.data?.reminders || [];
      setRows(list);
      const ids = list.map((r) => r.id);
      if (ids.length) remindersApi.statsSummary(ids).then((s) => {
        const m = {}; (s.data?.data?.summaries || []).forEach((x) => { m[x.reminder_id] = x; }); setStats(m);
      }).catch(() => {});
    } catch (e) {
      toast({ title: 'Could not load reminders', description: e?.response?.data?.message || e.message, status: 'error' });
      setRows([]);
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { all: rows.length, active: 0, draft: 0, paused: 0 };
    rows.forEach((r) => { if (c[r.status] != null) c[r.status] += 1; });
    return c;
  }, [rows]);

  const filtered = useMemo(() => rows.filter((r) =>
    (tab === 'all' || r.status === tab) &&
    (typeFilter === 'all' || r.type === typeFilter) &&
    (!query.trim() || r.name.toLowerCase().includes(query.trim().toLowerCase()))
  ), [rows, tab, typeFilter, query]);

  const act = async (fn, okMsg) => { try { await fn(); toast({ title: okMsg, status: 'success' }); load(); } catch (e) { toast({ title: 'Action failed', description: e?.response?.data?.message || e.message, status: 'error' }); } };
  const doDelete = () => act(() => remindersApi.remove(target.id), 'Reminder deleted');

  const dismissBanner = () => { setBanner(false); try { localStorage.setItem('reminders_sdk_banner', 'off'); } catch {} };

  return (
    <>
      <Header />
      <BoxWrapper>
        <PageHeading
          icon={LuAlarmClock}
          title="On-Device Reminders"
          subtitle="Notifications your app schedules on the phone, at a time relative to a date your app reports."
          mb={0}
        >
          {can('reminder-editor') && <Button leftIcon={<FiPlus />} size="sm" colorScheme="brand" onClick={picker.onOpen}>New reminder</Button>}
        </PageHeading>

        {banner && (
          <Flex mt={4} p={3} bg="blue.50" borderRadius="10px" align="center" justify="space-between" gap={2}>
            <Text fontSize="sm" color="blue.800">Reminders need app version with Push SDK 4.2.9 or later. Older app versions ignore them.</Text>
            <CloseButton size="sm" onClick={dismissBanner} />
          </Flex>
        )}

        <Flex mt={4} gap={2} wrap="wrap" align="center">
          {TABS.map(([k, label]) => (
            <Button key={k} size="sm" variant={tab === k ? 'solid' : 'ghost'} colorScheme={tab === k ? 'brand' : 'gray'} onClick={() => setTab(k)}>
              {label}<Badge ml={2} colorScheme={tab === k ? 'brand' : 'gray'}>{counts[k] ?? 0}</Badge>
            </Button>
          ))}
          <Box flex="1" />
          <Select size="sm" maxW="200px" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All types</option>
            {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          <InputGroup size="sm" maxW="240px"><InputLeftElement pointerEvents="none"><FiSearch color="#9aa0a6" /></InputLeftElement>
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search reminders…" /></InputGroup>
        </Flex>

        <TableContainer mt={4} borderWidth="1px" borderColor="#ebedf0" borderRadius="12px">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50"><Tr>
              <Th>Name</Th><Th>When</Th><Th>Audience</Th><Th>Status</Th><Th isNumeric>Scheduled (7d)</Th><Th>Delivered (7d)</Th><Th isNumeric>Tapped (7d)</Th><Th>Updated</Th><Th textAlign="right">Actions</Th>
            </Tr></Thead>
            <Tbody>
              {loading ? (<Tr><Td colSpan={9}><Center py={10}><Spinner /></Center></Td></Tr>)
                : filtered.length === 0 ? (<Tr><Td colSpan={9}><Center py={12}><Box textAlign="center" color="gray.400"><FiBell size={26} style={{ margin: '0 auto 8px' }} /><Text fontSize="sm" fontWeight={600}>No reminders yet.</Text><Text fontSize="xs" mt={1}>Create a rule like "remind users 24 hours before their bundle expires". Your app needs to report the event and its date first.</Text></Box></Center></Td></Tr>)
                  : filtered.map((r) => {
                    const sm = STATUS_META[r.status] || STATUS_META.draft;
                    const st = normalizeReminderStats(stats[r.id]);
                    const f = apiToForm(r);
                    return (
                      <Tr key={r.id} _hover={{ bg: 'gray.50' }} cursor="pointer" onClick={() => navigate(`/content/reminders/${r.id}`)}>
                        <Td><Text fontWeight={600}>{r.name}</Text>{f.event ? <Text fontSize="11px" color="gray.500" fontFamily="mono">{f.event}</Text> : null}</Td>
                        <Td maxW="240px"><Text fontSize="13px" color="gray.600" noOfLines={2}>{timingSentence(f) || '—'}</Text></Td>
                        <Td fontSize="13px" color="gray.600">{f.audMode === 'all' ? 'All users' : f.audMode === 'segments' ? (f.segments.join(', ') || 'Segments') : (f.tags.join(', ') || 'Tags')}</Td>
                        <Td><Tooltip label={sm.hint} openDelay={300}><Tag colorScheme={sm.scheme} size="sm">{sm.label}</Tag></Tooltip></Td>
                        <Td isNumeric>{formatCount(st.scheduled)}</Td>
                        <Td fontSize="12px" color="gray.600"><Tooltip label="iOS observed is a lower bound; estimated is not measured. They are separate figures, never added." openDelay={300}><span data-testid="list-delivered">{deliveredShort(st.delivered)}</span></Tooltip></Td>
                        <Td isNumeric>{st.tapped != null ? `${formatCount(st.tapped)} · ${formatPct(st.tapRate)}` : '—'}</Td>
                        <Td fontSize="12px" color="gray.500">{rel(r.updated_at)}</Td>
                        <Td textAlign="right" onClick={(e) => e.stopPropagation()}>
                          <HStack justify="flex-end" spacing={1}>
                            {can('reminder-editor') && <Tooltip label="Edit"><IconButton aria-label="Edit" icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => navigate(`/content/reminders/${r.id}`)} /></Tooltip>}
                            <Menu>
                              <MenuButton as={IconButton} aria-label="More" icon={<FiMoreVertical />} size="xs" variant="ghost" />
                              <MenuList>
                                <MenuItem icon={<FiBarChart2 />} onClick={() => navigate(`/content/reminders/${r.id}?view=performance`)}>View performance</MenuItem>
                                {can('reminder-publish') && r.status !== 'active' && <MenuItem icon={<FiSend />} onClick={() => act(() => remindersApi.publish(r.id), 'Published')}>Publish</MenuItem>}
                                {can('reminder-publish') && r.status === 'active' && <MenuItem icon={<FiPause />} onClick={() => act(() => remindersApi.pause(r.id), 'Paused')}>Pause</MenuItem>}
                                {can('reminder-publish') && r.status === 'paused' && <MenuItem icon={<FiPlay />} onClick={() => act(() => remindersApi.resume(r.id), 'Resumed')}>Resume</MenuItem>}
                                {can('reminder-editor') && <MenuItem icon={<FiCopy />} onClick={() => act(() => remindersApi.duplicate(r.id), 'Duplicated')}>Duplicate</MenuItem>}
                                {can('reminder-delete') && (
                                  <Tooltip label={r.status === 'active' ? 'Pause it first' : ''} isDisabled={r.status !== 'active'}>
                                    <MenuItem icon={<FiTrash2 />} color="red.500" isDisabled={r.status === 'active'} onClick={() => { setTarget(r); setConfirmOpen(true); }}>Delete</MenuItem>
                                  </Tooltip>
                                )}
                              </MenuList>
                            </Menu>
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })}
            </Tbody>
          </Table>
        </TableContainer>
      </BoxWrapper>
      <ConfirmDialog isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={doDelete} title="Delete reminder?" message={`"${target?.name}" will be removed. Reminders already on phones go away on their next app open.`} confirmLabel="Delete" />
      <ReminderTypePicker isOpen={picker.isOpen} onClose={picker.onClose} onPick={onPickType} />
    </>
  );
}
